// Entry points Blazor calls by name. Last in the bundle, so everything above is defined.

// Browse.razor, once the articles have rendered. `grayboxIds` is a comma separated list of
// the Overview / Quotes articles that get the desktop panel parallax; `readToken` signs the
// read-measurement beacons of this visit (none: nothing is measured); `recognition` is the
// visitor-recognition mode, which also gates the unique-device estimate.
window.startBrochure = (grayboxIds = '', readToken = null, recognition = 'off') => {
    const session = mag.startSession();

    document.documentElement.classList.add('show-scroll-section');

    // /browse/{name}#article-{id} — read before anything below scrolls the page
    const deepLinkId = /^#article-\d+$/.test(window.location.hash) ? window.location.hash.slice(1) : null;

    // One failing piece shouldn't leave the reader with a dead magazine, so each part is
    // started on its own.
    const run = (name, fn) => {
        try {
            fn();
        } catch (error) {
            console.error(`magazine: ${name} failed`, error);
        }
    };

    run('menu', () => mag.initMenu(session));

    // The layout-specific half, built through gsap.matchMedia: when the window crosses the
    // breakpoint (or reduced motion is switched), the old layout is torn down — its
    // ScrollTriggers, pins, listeners and inline styles — and the other one is built.
    let first = true;
    session.media.add({ desktop: mag.DESKTOP_QUERY, mobile: mag.MOBILE_QUERY, reduce: mag.REDUCE_QUERY }, (context) => {
        const { desktop, reduce } = context.conditions;
        const layout = mag.scope(session);
        // after a rebuild, stay on the article that was being read
        const resume = first ? deepLinkId : (mag.reader?.current ? `article-${mag.reader.current}` : null);

        if (desktop) {
            run('scroller', () => mag.initDesktopScroller(layout, resume));
            if (!reduce) {
                run('motion', () => mag.initDesktopMotion(layout, String(grayboxIds || '')));
            }
        } else {
            run('scroller', () => mag.initMobileScroller(layout));
            if (!reduce) {
                run('motion', () => mag.initMobileMotion(layout));
            }
        }
        run('headings', () => mag.initHeadings(layout, { reduce }));
        run('progress', () => mag.initProgress());

        if (!first && resume && mag.goToArticle) {
            mag.goToArticle(resume, false);
        }
        first = false;
        return () => layout.end();
    });

    // On mobile, lazy images arrive after the triggers were measured and push everything
    // below them down, so the triggers are measured again — but only once the page is
    // still: a refresh mid-scroll makes the motion jump.
    // Never on desktop: the articles there are sized in vh, an image doesn't move them,
    // and refreshing the pinned strip mid-scroll throws it thousands of pixels back.
    let remeasure = 0;
    let lastScroll = 0;
    const settle = () => {
        clearTimeout(remeasure);
        remeasure = setTimeout(() => {
            if (performance.now() - lastScroll < 400) {
                settle();
            } else if (mag.isMobile()) {
                ScrollTrigger.refresh();
            }
        }, 400);
    };
    window.addEventListener('scroll', () => lastScroll = performance.now(), { passive: true, signal: session.signal });
    document.addEventListener('load', (e) => {
        if (e.target instanceof HTMLImageElement && mag.isMobile()) {
            settle();
        }
    }, { capture: true, signal: session.signal });
    session.onEnd(() => clearTimeout(remeasure));

    run('navigation', () => mag.initArticleNavigation(session, deepLinkId));
    run('reader', () => mag.initReader(session, readToken, recognition));
    run('lightbox', () => mag.initLightbox(session));

    // Reveal step-section content now that everything is wired up, and let the articles
    // show (_effects.scss keeps them transparent until the entrances are in place, so the
    // first screen doesn't flash before it animates in).
    document.documentElement.classList.add('page-loaded');
    document.documentElement.classList.remove('fx-pending');
};

// Resolves once every stylesheet has applied. The page adds the brand sheet ({label}.css), which
// sizes the header and the articles, in the same render that calls init(), so on a cold cache it
// is usually still loading — and everything the layout measures would be measured unstyled.
const stylesApplied = () => Promise.race([
    Promise.all([...document.querySelectorAll('link[rel="stylesheet"]')]
        .filter(link => !link.sheet)
        .map(link => new Promise(resolve => {
            link.addEventListener('load', resolve, { once: true });
            link.addEventListener('error', resolve, { once: true });
        }))),
    // a sheet that failed before we got here never fires again
    new Promise(resolve => setTimeout(resolve, 3000)),
]);

let initCalls = 0;
window.init = async (grayboxIds = '', readToken = null, recognition = 'off') => {
    const call = ++initCalls;
    await stylesApplied();
    // a later init() (the page re-rendered meanwhile) takes over
    if (call === initCalls) {
        window.startBrochure(grayboxIds, readToken, recognition);
    }
    return true;
};

// The magazine is only known after the circuit connects, so <html lang> is corrected from
// here. CSS `hyphens: auto` resolves its dictionary from lang, so this must be called
// BEFORE the article content is rendered, otherwise text is laid out with the wrong
// hyphenation and reflows.
window.setLanguage = (language) => {
    document.documentElement.lang = language;
};
